import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Badge,
    Center,
    Container,
    Divider,
    Loader,
    Paper,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconCar,
    IconCheck,
    IconCircleX,
    IconClock,
} from "@tabler/icons-react";

import QRGenerator from "../../../components/QRGenerator";

type QrInfo = {
    isValid: boolean;
    message?: string;
    eventName?: string;
    parkingAreaName?: string;
    validDate?: string;
    isUsed?: boolean;
    usedAt?: string;
    note?: string;
};

export default function ParkingQrPassPage() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const webUrl =
        process.env.REACT_APP_WEB_URL || window.location.origin;

    const { token } = useParams<{ token: string }>();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<QrInfo | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        async function load() {
            setLoading(true);
            setError(null);

            try {
                if (!apiUrl || !token) {
                    throw new Error("Missing token or API URL.");
                }

                const response = await fetch(
                    `${apiUrl}/api/parking/qr/info/${token}`,
                    {
                        signal: controller.signal,
                        cache: "no-store",
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `HTTP ${response.status}`
                    );
                }

                const result: QrInfo =
                    await response.json();

                setData(result);
            } catch {
                if (!controller.signal.aborted) {
                    setError(
                        "Could not load parking pass."
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => controller.abort();
    }, [apiUrl, token]);

    const today = new Date();
    const todayString = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, "0"),
        String(today.getDate()).padStart(2, "0"),
    ].join("-");

    const isCorrectDate =
        data?.validDate?.substring(0, 10) === todayString;

    const isApproved =
        data?.isValid === true &&
        !data.isUsed &&
        isCorrectDate;

    const parkingArea =
        data?.parkingAreaName || "VIP Parking";

    return (
        <Container size="sm" py="xl">
            <Paper
                withBorder
                radius="xl"
                p="xl"
                shadow="sm"
            >
                {loading ? (
                    <Center py="xl">
                        <Stack align="center">
                            <Loader color="red" />
                            <Text>
                                Loading parking pass...
                            </Text>
                        </Stack>
                    </Center>
                ) : error || !data?.isValid ? (
                    <Stack align="center" gap="md">
                        <ThemeIcon
                            color="red"
                            size={72}
                            radius="xl"
                        >
                            <IconCircleX size={40} />
                        </ThemeIcon>

                        <Title order={2}>
                            PARKING PASS INVALID
                        </Title>

                        <Text c="dimmed" ta="center">
                            {error ||
                                data?.message ||
                                "Parking pass not found."}
                        </Text>
                    </Stack>
                ) : (
                    <Stack align="center" gap="md">
                        <ThemeIcon
                            color={isApproved ? "green" : "red"}
                            size={72}
                            radius="xl"
                        >
                            {isApproved ? (
                                <IconCar size={40} />
                            ) : (
                                <IconCircleX size={40} />
                            )}
                        </ThemeIcon>

                        <Title order={2} ta="center">
                            PARKING PASS
                        </Title>

                        <Badge
                            color={isApproved ? "green" : "red"}
                            size="xl"
                            variant="filled"
                        >
                            {data.isUsed
                                ? "ALREADY USED"
                                : isCorrectDate
                                    ? "READY TO SCAN"
                                    : "NOT VALID TODAY"}
                        </Badge>

                        <Title order={3} ta="center">
                            {parkingArea.toUpperCase()}
                        </Title>

                        <Text fw={600} ta="center">
                            {data.eventName}
                        </Text>

                        <Divider w="100%" />

                        <Stack align="center" gap="xs">
                            <IconClock
                                size={22}
                                color="gray"
                            />

                            <Text size="sm" c="dimmed">
                                VALID DATE
                            </Text>

                            <Text fw={700}>
                                {data.validDate}
                            </Text>
                        </Stack>

                        {isApproved && token && (
                            <Paper
                                withBorder
                                p="lg"
                                radius="lg"
                            >
                                <Stack align="center">
                                    <QRGenerator
                                        value={`${webUrl.replace(
                                            /\/$/,
                                            ""
                                        )}/parking-scan/${token}`}
                                    />

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                        ta="center"
                                    >
                                        Show this QR code
                                        at the parking entrance.
                                    </Text>
                                </Stack>
                            </Paper>
                        )}

                        {data.isUsed && (
                            <Stack align="center">
                                <IconCheck
                                    size={28}
                                    color="green"
                                />

                                <Text fw={600}>
                                    This parking pass has
                                    already been used.
                                </Text>

                                {data.usedAt && (
                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Used at:{" "}
                                        {new Date(
                                            data.usedAt
                                        ).toLocaleString("en-GB")}
                                    </Text>
                                )}
                            </Stack>
                        )}

                        {data.note && (
                            <Text
                                size="sm"
                                c="dimmed"
                                ta="center"
                            >
                                {data.note}
                            </Text>
                        )}
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}